import { useCallback } from 'react'
import { useDocumentLayout } from '@/contexts/document-layout-context'
import { useEditorCommandState } from '@/contexts/editor-command-state-context'
import { useEditorCommands } from '@/contexts/editor-commands-context'
import { useEditorSettings } from '@/contexts/editor-settings-context'
import { useEditorTools } from '@/contexts/editor-tools-context'
import { useTextDefaults } from '@/contexts/text-defaults-context'
import { runFreezeSelected } from '@/lib/editor/editor-command-handlers'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { CenterCanvasProps } from '../types/center-canvas-props'

/**
 * Merges `CenterCanvas`'s public props with the editor contexts into the one config object its
 * group hooks read. The derivations here are exactly the ones home applied when it passed these
 * values as props (pen tool on, point type, text tool, caption availability, etc.).
 */
export function useCenterCanvasConfig(props: CenterCanvasProps): CenterCanvasConfig {
  const layout = useDocumentLayout()
  const settings = useEditorSettings()
  const tools = useEditorTools()
  const textDefaults = useTextDefaults()
  const commands = useEditorCommands()
  const commandState = useEditorCommandState()
  const { removingBackgroundShapeIds } = commandState
  const { toggleShowGuides } = settings

  const isRemovingBackgroundShapeId = useCallback(
    (shapeId: string) => removingBackgroundShapeIds.has(shapeId),
    [removingBackgroundShapeIds]
  )
  const onShowGuidesRestore = useCallback(() => toggleShowGuides(true), [toggleShowGuides])

  return {
    ...props,
    // Document layout
    spreadId: layout.spreadId,
    checkpointLoadKey: layout.spreadId,
    pageViewMode: layout.pageViewMode,
    bleedWidth: layout.bleedWidth,
    bleedHeight: layout.bleedHeight,
    trimWidth: layout.trimWidth,
    trimHeight: layout.trimHeight,
    bleedMargin: layout.bleedMargin,
    liveInset: layout.liveInset,
    centerGap: layout.centerGap,
    marginInside: layout.marginInside,
    marginOutside: layout.marginOutside,
    marginTop: layout.marginTop,
    marginBottom: layout.marginBottom,
    mirrorSpineAxisX: layout.mirrorSpineAxisX,
    // Settings
    gridMode: settings.gridMode,
    snapToGrid: settings.snapToGrid,
    showBleed: settings.showBleed,
    showGuides: settings.showGuides,
    showLeftPageGuides: settings.showGuides,
    showRightPageGuides: settings.showGuides,
    showSmartGuides: settings.showSmartGuides,
    snapToGuides: settings.snapToGuides,
    snapGuideDistance: settings.snapGuideDistance,
    nudgeDistancePt: settings.nudgeDistancePt,
    showTextLinking: settings.showTextLinking,
    rulerUnit: settings.rulerUnit,
    unitOfMeasure: settings.unitOfMeasure,
    columns: settings.columns,
    rows: settings.rows,
    colGutter: settings.colGutter,
    rowGutter: settings.rowGutter,
    onShowGuidesRestore,
    // Tools
    activeItem: tools.activeItem,
    selectedShape: tools.selectedShape,
    isPenToolActive: tools.selectedPenTool === 'pen',
    penToolDefaultPointType: tools.selectedPointType === 'curve' ? 'smooth' : 'corner',
    isTextToolActive: tools.activeItem === 'type',
    isPanModeActive: tools.isPanModeActive,
    onDeactivatePanMode: tools.deactivatePanMode,
    mirrorMoveHoverActive: tools.isMirrorMoveHovering,
    // Text defaults
    newTextBoxKerning: textDefaults.newTextBoxKerning,
    newTextBoxTracking: textDefaults.newTextBoxTracking,
    newTextBoxUnderline: textDefaults.newTextBoxUnderline,
    newTextBoxAlignment: textDefaults.newTextBoxAlignment,
    defaultFontSize: textDefaults.defaultFontSize,
    defaultFontFamilyId: textDefaults.defaultFontFamilyId,
    defaultFontName: textDefaults.defaultFontName,
    // Commands and their availability
    onShapeClick: commands.canvasShapeClick,
    onBackgroundClick: commands.canvasBackgroundClick,
    onSelectionChange: commands.canvasSelectionChange,
    onShapeSelectionHistory: commands.shapeSelectionHistory,
    onSelectAllApplied: commands.selectAllApplied,
    onSelectNoneApplied: commands.selectNoneApplied,
    onCloseEditor: commands.closeEditor,
    onActivateTextTool: commands.activateTextTool,
    onActivateSelectTool: commands.activateSelectTool,
    onExpandTypePanel: commands.expandTypePanel,
    onShapeTextEditStateChange: commands.shapeTextEditStateChange,
    onDeleteShapesHandled: commands.deleteShapesHandled,
    onShapesNudge: commands.shapesNudge,
    onLiveSelectionGeometrySync: commands.liveSelectionGeometrySync,
    onCornerResizeGestureActiveChange: commands.cornerResizeGestureActiveChange,
    onCheckpointTextBoxesApplied: commands.checkpointTextBoxesApplied,
    getUndoStackSize: commands.getUndoStackSize,
    onNodeEditModeChange: commands.nodeEditModeChange,
    onNodeEditPointTypeChange: commands.nodeEditPointTypeChange,
    onToggleNodeEditPenMode: commands.toggleNodeEditPenMode,
    onExitPenTool: commands.exitPenTool,
    onConvertShapeToPath: commands.editPath,
    onEditTextPath: commands.editPath,
    onCheckSpelling: commands.checkSpelling,
    onConvertTextToShape: commands.convertTextToShape,
    onFreezeSelected: runFreezeSelected,
    onRemovePhotoFromShape: commands.removePhotoFromShape,
    onRemoveBackgroundFromShape: commands.removeBackgroundFromShape,
    isRemovingBackgroundShapeId,
    photoEffectsOpen: commandState.photoEffectsOpen,
    onPhotoEffectsOpenChange: commands.setPhotoEffectsOpen,
    onAddCaption: commandState.captionEnabled ? commands.addCaption : undefined,
  }
}
